
import React, { useState, useEffect, useRef } from "react";
import { Card, CardBody, Col, Button, Spinner, Container, Input } from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiGet, apiPost, mediaUrl } from "../../Utils";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Btn, H3, H5 } from "../../AbstractElements";
import { useNavigate } from "react-router-dom";
import { Edit, Plus, Trash2 } from 'react-feather';
import Swal from 'sweetalert2';
import { useAuth } from "../../Context/AuthContext";

export default function ViewMaintenance() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [refresh, setRefresh] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortColumn, setSortColumn] = useState("");
    const [sortDirection, setSortDirection] = useState("");
    const prevSearch = useRef(searchTerm);
    const navigate = useNavigate();
    const { hasPermission } = useAuth();

    useEffect(() => {
        const isSearchChanged = prevSearch.current !== searchTerm;
        const delay = isSearchChanged ? 500 : 0;
        prevSearch.current = searchTerm;

        const timer = setTimeout(() => {
            fetchData(currentPage, perPage, searchTerm, sortColumn, sortDirection);
        }, delay);
        return () => clearTimeout(timer);
    }, [refresh, currentPage, perPage, searchTerm, sortColumn, sortDirection]);

    const fetchData = async (page: number, size: number, search: string = "", sortCol: string = "", sortDir: string = "") => {
        setLoading(true);
        try {
            const body: any = {
                page: page,
                per_page: size,
                search: search
            };

            if (sortCol) {
                body.sort_by = sortCol;
                body.sort_direction = sortDir;
            }

            const response: any = await apiPost("maintenance/getall", body);
            if (response && response.data) {
                setData(response.data);
                setTotalRows(response.total);
            } else {
                setData(response);
                setTotalRows(response.length);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (column: any, sortDir: string) => {
        if (column.sortField) {
            setSortColumn(column.sortField);
            setSortDirection(sortDir);
            setCurrentPage(1);
        }
    };

    const handlePageChange = (page: number) => {
        setLoading(true);
        setCurrentPage(page);
    };

    const handlePerRowsChange = async (newPerPage: number, page: number) => {
        setLoading(true);
        setPerPage(newPerPage);
        setCurrentPage(page);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const handleAdd = () => {
        navigate(`${process.env.PUBLIC_URL}/add_maintenance`);
    };

    const handleEdit = (id: number) => {
        navigate(`${process.env.PUBLIC_URL}/edit_maintenance`, { state: { id: id } });
    };

    const handleDelete = async (id: number) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await apiPost("maintenance/delete", { id: id });
                    toast.success("Maintenance record deleted successfully", { autoClose: 1000 });
                    setRefresh(prev => prev + 1);
                } catch (error) {
                    toast.error("Failed to delete record", { autoClose: 1000 });
                }
            }
        });
    };

    const columns = React.useMemo(() => [
        {
            name: "Sl. No",
            selector: (row: any, index?: number) => (currentPage - 1) * perPage + (index || 0) + 1,
            width: "7%",
        },
        {
            name: "Reg No",
            selector: (row: any) => row.vehicle?.registration_number || 'N/A',
            sortable: true,
            sortField: "registration_number",
            wrap: true,
        },
        {
            name: "Branch",
            selector: (row: any) => row.branch?.branch_name || 'N/A',
            sortable: true,
            sortField: "branch_name",
            wrap: true,
        },
        {
            name: "Garage",
            selector: (row: any) => row.garage_name,
            sortable: true,
            sortField: "garage_name",
            wrap: true,
        },

        {
            name: "Next Service",
            selector: (row: any) => row.next_service_date ? row.next_service_date.split('T')[0] : '-',
            sortable: true,
            sortField: "next_service_date",
            cell: (row: any) => row.next_service_date ? new Date(row.next_service_date).toLocaleDateString('en-GB') : '-'
        },
        {
            name: "Cost",
            selector: (row: any) => row.total_cost || '-',
            sortable: true,
            sortField: "total_cost",
        },
        {
            name: "Invoice",
            cell: (row: any) => row.invoice_document ? (
                <a
                    href={`${mediaUrl}${row.invoice_document}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-outline-info btn-sm"
                >
                    View
                </a>
            ) : 'N/A',
            sortable: false,
        },
        {
            name: "Status",
            cell: (row: any) => {
                const status = row.status;
                let badgeClass = "bg-secondary";
                if (status === "Open" || status === "Pending") badgeClass = "bg-primary";
                else if (status === "In Progress") badgeClass = "bg-warning";
                else if (status === "Closed" || status === "Completed") badgeClass = "bg-success";

                return (
                    <span className={`badge ${badgeClass}`}>
                        {status}
                    </span>
                );
            },
            sortable: true,
            sortField: "status",
        },
        {
            name: "Actions",
            cell: (row: any) => (
                <div className="d-flex justify-content-between gap-2">

                    <Button size="sm" color="info" onClick={() => handleEdit(row.id)} title="Edit">
                        <Edit size={14} />
                    </Button>
                    {hasPermission('maintenance.delete') && (
                        <Button size="sm" color="danger" onClick={() => handleDelete(row.id)} title="Delete">
                            <Trash2 size={14} />
                        </Button>
                    )}
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
        },
    ], [currentPage, perPage]);

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#e0f2f1',
                color: '#00695c',
                minHeight: '50px',
                borderBottom: '1px solid #e0e0e0',
            },
        },
        headCells: {
            style: {
                fontSize: '15px',
                fontWeight: '600',
                paddingLeft: '16px',
                paddingRight: '16px',
            },
        },
        rows: {
            style: {
                minHeight: '50px',
                '&:not(:last-of-type)': {
                    borderBottomStyle: 'solid',
                    borderBottomWidth: '1px',
                    borderBottomColor: '#e0e0e0',
                },
            },
        },
    };

    return (
        <div className='page-body'>
            <Breadcrumbs pageTitle="Maintenance" parent="General" title="Maintenance" />
            <Container fluid>
                <Col xl={12} className="mb-lg-3">
                    <Card>
                        <CardBody>
                            <Col xs={12} className="mt-2 d-flex justify-content-between align-items-center mb-3">
                                <H3 className="text-primary">Maintenance Overview</H3>
                                {hasPermission('maintenance.add') && (
                                    <Btn color="primary" onClick={handleAdd}>
                                        <Plus size={16} /> Add Maintenance
                                    </Btn>
                                )}
                            </Col>
                            {hasPermission('maintenance.view') && (<>
                                <Col xs={12} className="mb-3 d-flex justify-content-end">
                                    <Input type="text" placeholder="Search..." value={searchTerm} onChange={handleSearchChange} style={{ maxWidth: "300px" }} />
                                </Col>
                                <div className="table-responsive">
                                    <DataTable
                                        data={data}
                                        columns={columns}
                                        pagination
                                        paginationServer
                                        paginationTotalRows={totalRows}
                                        onChangePage={handlePageChange}
                                        onChangeRowsPerPage={handlePerRowsChange}
                                        onSort={handleSort}
                                        sortServer
                                        progressPending={loading}
                                        progressComponent={<div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}><Spinner color="primary" /></div>}
                                        highlightOnHover
                                        customStyles={customStyles}
                                    />
                                </div>
                            </>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Container>
            <ToastContainer />
        </div>
    );
}
